import {
  DateRange,
  DateRangeInput,
  DATE_FORMATTER_STR,
  DATE_FORMAT_STR,
  formatDate,
  getRawDate,
  Select,
  sort,
  useLoading,
  ValueOpt,
  StatsTeam,
  useBreakpoints,
  Filters,
  DateDuration,
} from "best-common-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getDayOfRequests, getDepartments } from "../../../api/RequesTixApi";
import RouteConstants, { urlReplace } from "../../../constants/RouteConstants";
import TodaysTicketsDataTable, { DayOfTableData } from "../../../containers/admin/todaysTickets/TodaysTicketsDataTable";
import { useDropdowns } from "../../../contexts/DropdownsContext";
import { Department } from "../../../types/Department";
import { DayOfGameRequestDTO, DayOfRequestStatus } from "../../../types/TodaysTickets";
import { previousMonth } from "../../../util/DateObjUtil";
import { getDepartmentOption, getTeamOption } from "../../../util/DropdownUtil";
import { formatSubmittedDate } from "../../../util/TimeUtil";
import { ReportHandlerTypeProps } from "../ReportHandler";
import { useMetadata } from "../../../contexts/MetadataContext";

const ALL_STATUS_OPTION: ValueOpt<DayOfRequestStatus> = {
  label: "All",
  value: {},
} as ValueOpt<DayOfRequestStatus>;

const TodaysTicketsReport = ({ reportData, setReportData, setCanExport }: ReportHandlerTypeProps) => {
  const { setLoading } = useLoading();
  const { teamsOptions } = useDropdowns();
  const { isMobile, isTablet, isSmallDesktop } = useBreakpoints();
  const { dayOfRequestStatuses } = useMetadata();
  const { startDate, endDate, homeTeamId, departmentId, statusId } = reportData;
  const [departments, setDepartments] = useState<Department[]>([]);
  const [requests, setRequests] = useState<DayOfTableData[]>([]);

  const departmentOptions = useMemo(() => {
    return [
      {
        label: "All",
        value: {},
      },
    ].concat(
      sort(
        departments.map((d: Department) => ({
          label: d.departmentName,
          value: d,
        })),
        "label",
        1,
      ),
    );
  }, [departments]);
  const statusOptions: ValueOpt<DayOfRequestStatus>[] = useMemo(() => {
    return [ALL_STATUS_OPTION].concat(
      dayOfRequestStatuses.map((status: DayOfRequestStatus) => ({
        label: status.dayOfRequestStatus,
        value: status,
      })),
    );
  }, []);
  const selectedStatus: ValueOpt<DayOfRequestStatus> | undefined = useMemo(() => {
    return statusOptions.find((opt) => opt.value.dayOfRequestStatusId === statusId) ?? ALL_STATUS_OPTION;
  }, [statusId, statusOptions]);
  const dateRange: DateRange<Date> = useMemo(
    () => ({
      start: !!startDate ? getRawDate(startDate, DATE_FORMAT_STR) : previousMonth,
      end: !!endDate ? getRawDate(endDate, DATE_FORMAT_STR) : undefined,
    }),
    [startDate, endDate],
  );
  const canExport = useMemo(() => !!dateRange.start, [homeTeamId, dateRange]);
  const selectedHomeTeam = useMemo(() => getTeamOption(homeTeamId, teamsOptions), [homeTeamId, teamsOptions]);
  const maxDateRange: DateDuration = useMemo(
    () =>
      !!homeTeamId
        ? {
            months: 3,
          }
        : {
            weeks: 1,
          },
    [homeTeamId],
  );

  const onChange = useCallback(
    (key: string, value: any) => {
      setReportData((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [setReportData],
  );

  const updateDate = useCallback(
    ({ start, end }: DateRange<Date>) => {
      if (!!start) {
        setReportData((prev) => ({
          ...prev,
          startDate: formatDate(start, DATE_FORMATTER_STR),
          endDate: !!end ? formatDate(end, DATE_FORMATTER_STR) : undefined,
        }));
      }
    },
    [setReportData],
  );

  const getData = useCallback(
    async (startDate: Date, endDate?: Date, teamId?: number, departmentId?: number, statusId?: number) => {
      try {
        setLoading(true);
        if (canExport) {
          const res: DayOfGameRequestDTO[] = await getDayOfRequests(
            formatDate(startDate),
            !!endDate ? formatDate(endDate) : undefined,
            teamId,
            departmentId,
            statusId,
          );
          const data: DayOfTableData[] = res.map((req: DayOfGameRequestDTO) => ({
            dayOfRequestId: req.dayOfRequestId,
            editLink: urlReplace(RouteConstants.ADMIN.TODAYS_TICKETS_EDIT, { requestId: req.dayOfRequestId }),
            firstName: req.user.firstName,
            lastName: req.user.lastName,
            ballparkEmail: req.emailAddress,
            numberOfTickets: req.numberOfTickets,
            game: req.game,
            ticketsSentDisplay: req.ticketSentDisplay,
            status: req.status,
            requestTime: formatSubmittedDate(req.requestedDate),
            statusId: req.statusId,
            stadium: req.game?.venue?.name ?? "",
          }));
          setRequests(data);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [canExport, homeTeamId, dateRange, departmentId, setLoading],
  );

  useEffect(() => {
    if (canExport) {
      void getData(dateRange.start!, dateRange.end, homeTeamId, departmentId, statusId);
    }
  }, [dateRange, homeTeamId, departmentId, statusId, canExport]);

  useEffect(() => {
    getDepartments().then((data: Department[]) => {
      setDepartments(data);
    });
  }, []);

  useEffect(() => {
    setCanExport(canExport);
  }, [canExport]);

  return (
    <>
      <Filters numberDisplayed={isMobile ? 1 : isTablet ? 2 : isSmallDesktop ? 3 : 4}>
        <Select
          id="home-team-dropdown"
          label="Home Team"
          options={teamsOptions}
          value={selectedHomeTeam}
          onChange={(team: ValueOpt<StatsTeam>) => onChange("homeTeamId", team.value.id || null)}
          gutterBottom
        />
        <DateRangeInput
          id="game-date"
          label="Game Date"
          value={dateRange}
          onChange={updateDate}
          maxRange={maxDateRange}
          withPortal
          gutterBottom
          rangeToggle
        />
        <Select
          id="department"
          label="Department"
          options={departmentOptions}
          value={getDepartmentOption(departmentId!, departmentOptions)}
          onChange={(value) => {
            onChange("departmentId", value?.value?.departmentId!);
          }}
          gutterBottom
        />
        <Select
          id="status"
          label="Status"
          options={statusOptions}
          value={selectedStatus}
          onChange={(value) => {
            onChange("statusId", value?.value?.dayOfRequestStatusId!);
          }}
          gutterBottom
        />
      </Filters>
      <TodaysTicketsDataTable requests={requests} />
    </>
  );
};

export default TodaysTicketsReport;
